import { OriginBadge } from "@/components/Origin";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { fileUrl } from "@/lib/api";
import { CARD_H, CARD_W, isDeceased, roleLabel } from "@/lib/family";

const MODES = {
  parent: { label: "Add parent", roles: ["parent", "adoptive_parent"] },
  child: { label: "Add child", roles: ["child", "adoptive_child"] },
  sibling: { label: "Add sibling", roles: ["sibling", "half_sibling"] },
  partner: { label: "Add partner", roles: [] },
};
const initials = (t) => t.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const AddForm = ({ mode, self, chars, onDone, onBack, act }) => {
  const [q, setQ] = useState("");
  const [role, setRole] = useState(MODES[mode].roles[0]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const opts = chars.filter((c) => c.id !== self.id && c.title.toLowerCase().includes(q.toLowerCase()));
  const exact = chars.some((c) => c.title.toLowerCase() === q.trim().toLowerCase());
  const go = async (target) => { setBusy(true); await act.addRelative(self, mode, target, role, note); setBusy(false); onDone(); };
  return (
    <div className="space-y-2" data-testid={`family-add-${mode}-form`}>
      <button onClick={onBack} className="text-xs text-slate-500 hover:text-slate-200 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> {MODES[mode].label}</button>
      {MODES[mode].roles.length > 0 && (
        <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full h-8 rounded-md bg-canvas border border-line text-sm px-2" data-testid="family-role-select">
          {MODES[mode].roles.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
        </select>
      )}
      {mode === "partner" && <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={120} placeholder="Note, e.g. endgame" className="h-8 text-sm" data-testid="family-partner-note" />}
      <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search or type a new name" className="h-8 text-sm" data-testid="family-search-input" />
      <div className="max-h-44 overflow-y-auto">
        {opts.map((c) => (
          <button key={c.id} disabled={busy} onClick={() => go(c)} data-testid={`family-pick-${c.id}`} className="block w-full text-left text-sm px-2 py-1.5 rounded text-slate-300 hover:bg-amber-500/10 hover:text-amber-200 truncate">{c.title}</button>
        ))}
      </div>
      {q.trim() && !exact && (
        <button disabled={busy} onClick={() => go(q.trim())} data-testid="family-create-btn" className="w-full text-left text-sm px-2 py-1.5 rounded text-amber-300 bg-amber-500/10 hover:bg-amber-500/20 flex items-center gap-1.5">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Create “{q.trim()}”
        </button>
      )}
    </div>
  );
};

const RemoveList = ({ self, records, byId, act, onBack }) => {
  const mine = records.filter((r) => r.src.id === self.id || r.target === self.id);
  return (
    <div className="space-y-1.5" data-testid="family-remove-list">
      <button onClick={onBack} className="text-xs text-slate-500 hover:text-slate-200 flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> Remove a link</button>
      {mine.length === 0 && <p className="text-xs text-slate-500 italic">No family links yet.</p>}
      {mine.map((r, i) => {
        const other = byId.get(r.src.id === self.id ? r.target : r.src.id);
        const kind = r.kind === "couple" ? "partner" : `${roleLabel(r.role).toLowerCase()} (on ${r.src.title})`;
        return (
          <div key={i} className="flex items-center gap-2 text-sm text-slate-300" data-testid={`family-remove-row-${i}`}>
            <span className="flex-1 min-w-0 break-words">{other?.title} <span className="block text-[11px] font-code text-slate-500">{kind}</span></span>
            <button onClick={() => act.removeRecord(r)} className="text-slate-500 hover:text-rose-400" data-testid={`family-remove-btn-${i}`}><X className="w-3.5 h-3.5" /></button>
          </div>
        );
      })}
    </div>
  );
};

const Menu = (props) => {
  const [view, setView] = useState("menu");
  const { self, basePath, close } = props;
  if (view === "remove") return <RemoveList {...props} onBack={() => setView("menu")} />;
  if (MODES[view]) return <AddForm {...props} mode={view} onBack={() => setView("menu")} onDone={close} />;
  return (
    <div className="flex flex-col" data-testid="family-card-menu">
      <p className="font-display text-lg text-amber-100 px-2 pb-1 truncate">{self.title}</p>
      {Object.entries(MODES).map(([k, m]) => (
        <button key={k} onClick={() => setView(k)} data-testid={`family-menu-${k}`} className="text-left text-sm px-2 py-1.5 rounded text-slate-300 hover:bg-amber-500/10 hover:text-amber-200">{m.label}</button>
      ))}
      <Link to={`${basePath}/p/${self.id}`} className="text-sm px-2 py-1.5 rounded text-slate-300 hover:bg-amber-500/10 hover:text-amber-200" data-testid="family-menu-open">Open page</Link>
      <button onClick={() => setView("remove")} data-testid="family-menu-remove" className="text-left text-sm px-2 py-1.5 rounded text-rose-300 hover:bg-rose-500/10">Remove a link…</button>
    </div>
  );
};

export const FamilyCard = ({ page, p, dim, onHover, readOnly, basePath, i, ...rest }) => {
  const [open, setOpen] = useState(false);
  const body = (
    <div onPointerDown={(e) => e.stopPropagation()} onMouseEnter={() => onHover(page.id)} onMouseLeave={() => onHover(null)} data-testid={`family-card-${page.id}`}
      style={{ left: p.x, top: p.y, width: CARD_W, height: CARD_H, animationDelay: `${Math.min(i, 20) * 35}ms` }}
      className={`rise absolute rounded-xl border border-line bg-panel flex flex-col items-center justify-center gap-1.5 px-2 transition-opacity duration-300 ${dim ? "opacity-25" : ""} ${readOnly ? "" : "cursor-pointer hover:border-amber-500/50"}`}>
      {page.image ? <img src={fileUrl(page.image)} alt="" className="w-14 h-14 rounded-lg object-cover border border-line" />
        : <div className="w-14 h-14 rounded-lg bg-amber-500/10 text-amber-300 font-display text-xl flex items-center justify-center">{initials(page.title)}</div>}
      <Link to={`${basePath}/p/${page.id}`} onClick={(e) => e.stopPropagation()} className="font-display text-[15px] leading-tight text-center text-slate-100 hover:text-amber-300 line-clamp-2" data-testid={`family-card-name-${page.id}`}>
        {page.title}{isDeceased(page) && <span className="text-slate-500 ml-1" data-testid="family-deceased">†</span>} <OriginBadge page={page} />
      </Link>
    </div>
  );
  if (readOnly) return body;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{body}</PopoverTrigger>
      <PopoverContent className="w-72 p-2 bg-panel border-line" onPointerDown={(e) => e.stopPropagation()}>
        <Menu self={page} basePath={basePath} close={() => setOpen(false)} {...rest} />
      </PopoverContent>
    </Popover>
  );
};
